/* ═══════════════════════════════════════════════════════════════
   People — shared style constants + helpers. Self-contained copy
   of the field/layout style objects from pages/team/styles.tsx
   (token-driven, no raw hex/px) plus the name-split display/sort
   helpers this section needs everywhere a person's name is shown
   or ordered (docs/PEOPLE_REBUILD_PLAN.md §0 grant #1).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { TeamMember } from '../../../shared/types';
import { rem } from '../../lib/rem';

export const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: `${rem(7)} ${rem(10)}`,
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  boxSizing: 'border-box',
};

export const fieldBlock: React.CSSProperties = {
  display: 'block',
  marginBottom: 'var(--space-3)',
};

export const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  color: 'var(--tm)',
  marginBottom: 'var(--space-1)',
};

export const sectionHeading: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  marginBottom: rem(10),
};

export const emptyPill: React.CSSProperties = {
  padding: `${rem(10)} ${rem(14)}`,
  textAlign: 'center',
  background: 'var(--sf)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  color: 'var(--tm)',
  fontSize: 'var(--font-size-base)',
  fontStyle: 'italic',
};

export function PlusIcon() {
  return (
    <svg
      width={rem(13)}
      height={rem(13)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

// ─── Name split helpers (People Rebuild grant #1) ───

/** THE ONE combine helper — first + middle-if-present + last, space-joined,
 *  blank parts dropped. Mirrors the server's dbSaveTeamMember rebuild logic
 *  (ec2-api/server.js) exactly, so client-derived display strings and the
 *  canonical server-maintained `name` column never disagree. Every full-name
 *  DISPLAY site in the app should route through this (or through
 *  `displayName`/`nameSortKey` below, which call it) — never hand-concatenate
 *  first/last again (No Full-Name Input/Write Law, 2026-08-19). */
export function combineName(first?: string, middle?: string, last?: string): string {
  return [first, middle, last]
    .map((v) => v?.trim())
    .filter(Boolean)
    .join(' ');
}

/** "First Middle Last" for display — falls back to the legacy `name` column
 *  when no split parts are populated yet (shouldn't happen post-migration-038
 *  backfill, but keeps the UI safe for any row the backfill missed). */
export function displayName(m: TeamMember): string {
  const combined = combineName(m.firstName, m.middleName, m.lastName);
  return combined || m.name || '';
}

/** Sort/search key: last name first, so the Name column sorts by last name
 *  then first — the whole point of the split (docs/PEOPLE_REBUILD_PLAN.md
 *  §5). Falls back to the legacy `name` column the same way displayName does. */
export function nameSortKey(m: TeamMember): string {
  const last = m.lastName?.trim();
  const first = m.firstName?.trim();
  if (last || first) return `${last || ''} ${first || ''}`.trim().toLowerCase();
  return (m.name || '').toLowerCase();
}

/** Splits a single free-text name into first/middle/last using the same
 *  convention as the migration 038 backfill: first token → first, last
 *  token → last, remainder → middle; single token → first only. No UI
 *  surface types into this anymore (No Full-Name Input/Write Law,
 *  2026-08-19) — it exists only for BOUNDARIES that receive a single name
 *  string from outside the split system (e.g. AdminPage's CSV team import,
 *  which only ever offers one "Name" column to map). */
export function splitName(full: string): {
  firstName?: string;
  middleName?: string;
  lastName?: string;
} {
  const tokens = full.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return {};
  if (tokens.length === 1) return { firstName: tokens[0] };
  return {
    firstName: tokens[0],
    lastName: tokens[tokens.length - 1],
    middleName: tokens.length > 2 ? tokens.slice(1, -1).join(' ') : undefined,
  };
}
